import './index.css'
import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, X, Check, Receipt, Lock, Archive, ArchiveRestore, MoreVertical } from 'lucide-react'
import type { CardsPanelProps, Card } from '../../types'
import { fmt, maskCurrency, digitsToAmount } from '../../lib/format'
import { can } from '../../lib/features'

type CardForm = { name: string; last4: string; tone: string; limitDigits: string; due: string; close: string; is_archived: boolean }

const PRESET_COLORS: string[] = [
  '#1F3D2E', '#242321', '#B43F30', '#B47812', '#FF6B00', '#CC1429',
  '#6A05A8', '#003D87', '#0070BB', '#1FA55E', '#006937', '#00C1B4',
]
const BLANK: CardForm = { name: '', last4: '', tone: '#1F3D2E', limitDigits: '', due: '', close: '', is_archived: false }

function toCard(f: CardForm): Omit<Card, 'id'> {
  return {
    name:  f.name.trim(),
    last4: f.last4.trim() || null,
    tone:  f.tone,
    bill:  0,
    limit: digitsToAmount(f.limitDigits),
    due:   parseInt(f.due.trim(), 10),
    close: parseInt(f.close.trim(), 10),
    is_archived: f.is_archived,
  }
}

function validate(f: CardForm): string | null {
  if (!f.name.trim())                     return 'Nome obrigatório'
  if (f.last4.trim() && !/^\d{4}$/.test(f.last4.trim())) return 'Informe exatamente 4 dígitos'
  const due = parseInt(f.due.trim(), 10)
  if (isNaN(due) || due < 1 || due > 31)  return 'Dia de vencimento inválido (1–31)'
  const close = parseInt(f.close.trim(), 10)
  if (isNaN(close) || close < 1 || close > 31) return 'Dia de fechamento inválido (1–31)'
  return null
}

export default function CardsPanel({ cards, transactions, onAdd, onEdit, onDelete, onViewInvoice, planTier = 'free', onUpgrade }: CardsPanelProps) {
  const [form,      setForm]      = useState<CardForm>(BLANK)
  const [editId,    setEditId]    = useState<string | null>(null)
  const [showForm,  setShowForm]  = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [saving,    setSaving]    = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [openMenu,  setOpenMenu]  = useState<string | null>(null)

  useEffect(() => {
    const close = () => setOpenMenu(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])

  const activeCards   = cards.filter(c => !c.is_archived)
  const archivedCards = cards.filter(c => c.is_archived)

  const maxCards = can('maxCards', planTier) as number
  const atLimit  = activeCards.length >= maxCards

  function openAdd() {
    setForm(BLANK); setEditId(null); setError(null); setShowForm(true)
  }

  function openEdit(c: Card) {
    setForm({ name: c.name, last4: c.last4 ?? '', tone: c.tone,
              limitDigits: Math.round(c.limit * 100).toString(),
              due: String(c.due), close: String(c.close), is_archived: c.is_archived })
    setEditId(c.id); setError(null); setShowForm(true)
  }

  function closeForm() {
    setShowForm(false)
    setEditId(null)
    setError(null)
  }

  function cancel() {
    closeForm()
  }

  async function save() {
    const err = validate(form)
    if (err) { setError(err); return }
    setSaving(true); setError(null)
    try {
      editId ? await onEdit(editId, toCard(form)) : await onAdd(toCard(form))
      closeForm()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Remover este cartão?')) return
    try {
      await onDelete(id)
    } catch {
      /* global toast handled by onDelete */
    }
  }

  const set = (k: keyof CardForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm(p => ({ ...p, [k]: e.target.value }))

  const setCurrency = (k: 'limitDigits') =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(p => ({ ...p, [k]: e.target.value.replace(/\D/g, '').slice(0, 12) }))

  function CardRow({ c, dimmed }: { c: Card; dimmed?: boolean }) {
    const fatura = transactions.filter(t => t.card_id === c.id).reduce((s, t) => s + t.amount, 0)
    const isExpanded = openMenu === c.id
    return (
      <div className={`bc-card-row-wrap${isExpanded ? ' is-expanded' : ''}`} style={dimmed ? { opacity: 0.6 } : undefined}>
        <div className="bc-card-row" onClick={() => onViewInvoice(c)}>
          <div className="bc-chip" style={{ background: c.tone }} aria-hidden="true">
            <span className="bc-chip__digits">
              {c.last4
                ? <><span className="dots">••</span>{c.last4}</>
                : <span className="dots">••••</span>}
            </span>
          </div>
          <div className="bc-info">
            <span className="bc-info__name">{c.name}{dimmed ? ' (Arquivado)' : ''}</span>
            <span className="bc-info__meta">Fecha dia {c.close} · Vence dia {c.due}</span>
          </div>
          <div className="bc-money">
            <span className="bc-money__amt"><span className="cur">R$</span>{fmt(fatura)}</span>
            <button className="bc-money__link" onClick={e => { e.stopPropagation(); onViewInvoice(c) }}>
              <Receipt size={11} /> Ver fatura
            </button>
          </div>
          {/* Desktop: inline action buttons visible on hover */}
          <div className="bc-card-row__actions">
            <button className="bc-btn bc-btn--icon" onClick={e => { e.stopPropagation(); onViewInvoice(c) }} title="Ver fatura">
              <Receipt size={13} />
            </button>
            <button className="bc-btn bc-btn--icon" onClick={e => { e.stopPropagation(); openEdit(c) }} title="Editar">
              <Pencil size={13} />
            </button>
            <button className="bc-btn bc-btn--icon bc-btn--danger" onClick={e => { e.stopPropagation(); handleDelete(c.id) }} title="Remover">
              <Trash2 size={13} />
            </button>
          </div>
          {/* Mobile: kebab toggles expand row */}
          <button
            className={`bc-kebab${isExpanded ? ' is-open' : ''}`}
            onClick={e => { e.stopPropagation(); setOpenMenu(isExpanded ? null : c.id) }}
            aria-label="Mais ações"
          >
            <MoreVertical size={16} />
          </button>
        </div>
        {/* Mobile expand actions (CSS-animated, no overflow issues) */}
        <div className="bc-row-actions">
          <button className="bc-row-actions__btn" onClick={e => { e.stopPropagation(); onViewInvoice(c); setOpenMenu(null) }}>
            <Receipt size={14} /> Ver fatura
          </button>
          <button className="bc-row-actions__btn" onClick={e => { e.stopPropagation(); openEdit(c); setOpenMenu(null) }}>
            <Pencil size={14} /> Editar
          </button>
          <button className="bc-row-actions__btn is-danger" onClick={e => { e.stopPropagation(); handleDelete(c.id); setOpenMenu(null) }}>
            <Trash2 size={14} /> Excluir
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bc-card bc-cards" data-tour="cards-panel">
      <div className="bc-cards__head">
        <h3 className="bc-cards__title">Cartões de crédito</h3>
        {atLimit ? (
          <button className="bc-cards__new" onClick={onUpgrade} title="Faça upgrade para adicionar mais cartões">
            <Lock size={14} /> Pro
          </button>
        ) : (
          <button className="bc-cards__new" onClick={openAdd}>
            <Plus size={14} /> Novo
          </button>
        )}
      </div>

      {activeCards.length === 0 && !showForm && (
        <p className="bc-empty">Nenhum cartão ativo cadastrado.</p>
      )}

      {activeCards.map(c => <CardRow key={c.id} c={c} />)}

      {archivedCards.length > 0 && (
        <div className="bc-cards__archived-section">
          <button
            className="bc-btn bc-btn--ghost bc-btn--sm"
            style={{ margin: '12px auto', display: 'flex', width: 'fit-content' }}
            onClick={() => setShowArchived(p => !p)}
          >
            {showArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            <span>{showArchived ? 'Ocultar cartões arquivados' : `Ver cartões arquivados (${archivedCards.length})`}</span>
          </button>

          {showArchived && archivedCards.map(c => <CardRow key={c.id} c={c} dimmed />)}
        </div>
      )}

      {showForm && (
        <div className="bc-card-form">
          <div className="bc-card-form__grid">
            <div className="bc-field">
              <label className="bc-label">Banco / nome</label>
              <input className="bc-input" value={form.name} onChange={set('name')}
                maxLength={40} placeholder="Nubank" autoFocus />
            </div>
            <div className="bc-field">
              <label className="bc-label">Últimos 4 dígitos <span className="bc-label__opt">(opcional)</span></label>
              <input className="bc-input" value={form.last4} onChange={set('last4')}
                maxLength={4} placeholder="1234" inputMode="numeric" />
            </div>
            <div className="bc-field">
              <label className="bc-label">Limite</label>
              <div className="bc-currency">
                <span className="bc-currency__prefix">R$</span>
                <input value={maskCurrency(form.limitDigits)} onChange={setCurrency('limitDigits')}
                  placeholder="0,00" inputMode="numeric" />
              </div>
            </div>
            <div className="bc-field">
              <label className="bc-label">Dia do fechamento</label>
              <input className="bc-input" value={form.close}
                onChange={e => setForm(p => ({ ...p, close: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                maxLength={2} placeholder="5" inputMode="numeric" />
            </div>
            <div className="bc-field">
              <label className="bc-label">Dia do vencimento</label>
              <input className="bc-input" value={form.due}
                onChange={e => setForm(p => ({ ...p, due: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                maxLength={2} placeholder="15" inputMode="numeric" />
            </div>
            <div className="bc-field" style={{ gridColumn: '1 / -1' }}>
              <label className="bc-label">Cor</label>
              <div className="bc-cc-grid">
                {PRESET_COLORS.map(color => (
                  <button key={color} type="button"
                    className={'bc-cc-mini' + (form.tone === color ? ' bc-cc-mini--sel' : '')}
                    style={{ background: color }}
                    onClick={() => setForm(p => ({ ...p, tone: color }))}
                    aria-label={color}
                  />
                ))}
                <label
                  className={'bc-cc-mini bc-cc-mini--custom' + (!PRESET_COLORS.includes(form.tone) ? ' bc-cc-mini--sel' : '')}
                  style={!PRESET_COLORS.includes(form.tone) ? { background: form.tone } : undefined}
                  title="Cor personalizada"
                >
                  {PRESET_COLORS.includes(form.tone) && <span className="bc-cc-mini__plus">+</span>}
                  <input type="color" className="bc-cc-mini__input"
                    value={PRESET_COLORS.includes(form.tone) ? '#000000' : form.tone}
                    onChange={e => setForm(p => ({ ...p, tone: e.target.value }))}
                  />
                </label>
              </div>
            </div>
            {editId && (
              <div className="bc-field" style={{ gridColumn: '1 / -1' }}>
                <label className="bc-checkbox" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={form.is_archived}
                    onChange={e => setForm(p => ({ ...p, is_archived: e.target.checked }))}
                  />
                  <span>Arquivar este cartão (oculta da listagem principal e novo lançamento)</span>
                </label>
              </div>
            )}
          </div>

          {error && <p className="bc-field__error" style={{ marginTop: 8 }}>{error}</p>}

          <div className="bc-card-form__footer">
            <button className="bc-btn bc-btn--ghost" onClick={cancel} disabled={saving}>
              <X size={14} /> Cancelar
            </button>
            <button className="bc-btn bc-btn--primary" onClick={save} disabled={saving}>
              <Check size={14} /> {saving ? '…' : editId ? 'Salvar' : 'Adicionar'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
